import Vue from 'vue'
import Vuex, {ActionContext} from 'vuex'
import {FilterInterface} from "@/MarketSearch/interfaces/FilterInterface";
import {LocationInterface} from "@/shared/interfaces/LocationInterface";
import {LocationFilter} from "@/MarketSearch/helpers/LocationFilter";
import {UserLocation} from "@/shared/services/UserLocation";
import {google} from "google-maps";

Vue.use(Vuex)

export type ConfigParameter = {
    favoriteFilter: string,
    configurableLink?: string,
    sortByMarket: string,
}

export interface StoreInterface {
    searchTerm: string,
    filters: Set<FilterInterface>,
    features: Set<FilterInterface>,
    activeFilters: Set<FilterInterface>,
    activeFeatures: Set<FilterInterface>,
    locations: Set<LocationInterface>,
    filteredLocations: Set<LocationInterface>,
    mapsAPIKey: string,
    favorite: string | null,
    config: ConfigParameter,
    maxShownLocations: number,
    map: google.maps.Map | null
}

type StoreActionContext = ActionContext<StoreInterface, any>

export const store = new Vuex.Store({
    state: {
        searchTerm: '',
        filters: new Set(),
        features: new Set(),
        activeFilters: new Set(),
        activeFeatures: new Set(),
        locations: new Set(),
        filteredLocations: new Set<LocationInterface>(),
        mapsAPIKey: '',
        favorite: UserLocation.get(),
        config: {},
        maxShownLocations: -1,
        map: null,
    },
    mutations: {
        SET_SEARCH_TERM(state: StoreInterface, payload: string) {
            state.searchTerm = payload;
        },
        SET_FILTERS(state: StoreInterface, payload: Set<FilterInterface>) {
            state.filters = payload;
        },
        SET_FEATURES(state: StoreInterface, payload: Set<FilterInterface>) {
            state.features = payload;
        },
        SET_ACTIVE_FILTERS(state: StoreInterface, payload: Set<FilterInterface>) {
            state.activeFilters = new Set();
            state.filters.forEach(filter => {
                if (payload.has(filter)) {
                    state.activeFilters.add(filter);
                }
            })
        },
        SET_ACTIVE_FEATURES(state: StoreInterface, payload: Set<FilterInterface>) {
            state.activeFeatures = new Set();
            state.features.forEach(filter => {
                if (payload.has(filter)) {
                    state.activeFeatures.add(filter);
                }
            })
        },
        SET_LOCATIONS(state: StoreInterface, payload: Set<LocationInterface>) {
            state.locations = payload;
        },
        SET_MAPS_API_KEY(state: StoreInterface, payload: string) {
            state.mapsAPIKey = payload;
        },
        SET_FAVORITE(state: StoreInterface, payload: string) {
            state.favorite = payload;
        },
        SET_CONFIG(state: StoreInterface, payload: ConfigParameter) {
            state.config = payload;
        },
        SET_MAX_SHOWN_LOCATIONS(state: StoreInterface, payload: number) {
            state.maxShownLocations = payload;
        },
        SET_FILTERED_LOCATIONS(state: StoreInterface, payload: Set<LocationInterface>) {
            state.filteredLocations = payload;
        },
        SET_MAP(state: StoreInterface, payload: google.maps.Map) {
            state.map = payload;
        }
    },
    actions: {
        setSearchTerm({commit, dispatch}: StoreActionContext, payload: string) {
            commit('SET_SEARCH_TERM', payload);
            dispatch('filterList');
        },
        setFilters({commit, dispatch}: StoreActionContext, payload: Set<FilterInterface>) {
            commit('SET_FILTERS', payload);
            dispatch('filterList');
        },
        setFeatures({commit, dispatch}: StoreActionContext, payload: Set<FilterInterface>) {
            commit('SET_FEATURES', payload);
            dispatch('filterList');
        },
        setLocations({commit, dispatch}: StoreActionContext, payload: Set<LocationInterface>) {
            const sortByMarket = this.state.config.sortByMarket.split(',');
            let payloadArray = Array.from(payload);


            payloadArray = payloadArray.sort((locationA: LocationInterface, locationB: LocationInterface) => {
                if (locationA.distance) return 0
                if (locationA.keywords?.locationType === locationB.keywords?.locationType) return 0;
                if (sortByMarket.includes(locationA.keywords?.locationType)) return -1;
                if (sortByMarket.includes(locationB.keywords?.locationType)) return 1;
                return 0;
            })

            commit('SET_LOCATIONS', new Set(payloadArray));
            dispatch('filterList');

        },
        setActiveFilters({commit, state, dispatch}: StoreActionContext, payload: Set<FilterInterface>) {
            const activeFilters = state.activeFilters;
            let newElementFound = activeFilters.size != payload.size;

            if (!newElementFound) {
                payload.forEach(filter => {
                    if (!newElementFound && !activeFilters.has(filter)) {
                        newElementFound = true;
                    }
                })
            }
            if (newElementFound) {
                commit('SET_ACTIVE_FILTERS', payload);
            }
            dispatch('filterList');
        },
        setActiveFeatures({commit, dispatch}: StoreActionContext, payload: Set<FilterInterface>) {
            commit('SET_ACTIVE_FEATURES', payload);
            dispatch('filterList');
        },
        setMapsAPIKey({commit}: StoreActionContext, payload: string) {
            commit('SET_MAPS_API_KEY', payload);
        },
        setFavorite({commit}: StoreActionContext, payload: string | null) {
            commit('SET_FAVORITE', payload);
        },
        setConfig({commit}: StoreActionContext, payload: ConfigParameter) {
            commit('SET_CONFIG', Object.assign({}, payload));
        },
        setMaxShownLocations({commit, dispatch}: StoreActionContext, payload: number) {
            commit('SET_MAX_SHOWN_LOCATIONS', payload);
            dispatch('filterList');
        },
        filterList({commit, state}: StoreActionContext) {
            const locationFilter = new LocationFilter();
            const filteredLocations = locationFilter.filter(
                state.locations,
                '',
                state.activeFilters,
                state.activeFeatures
            );
            commit('SET_FILTERED_LOCATIONS', filteredLocations);
        },
        setMap({commit}, payload: google.maps.Map) {
            commit('SET_MAP', payload);
        }
    },
    getters: {
        filters(state: StoreInterface): Set<FilterInterface> {
            return state.filters;
        },
        features(state: StoreInterface): Set<FilterInterface> {
            return state.features;
        },
        activeFilters(state: StoreInterface): Set<FilterInterface> {
            return state.activeFilters;
        },
        activeFeatures(state: StoreInterface): Set<FilterInterface> {
            return state.activeFeatures;
        },
        filteredLocations(state: StoreInterface, getters): Set<LocationInterface> {
            const sortByMarket = state.config.sortByMarket.split(',');
            let locations = Array.from(state.filteredLocations);
            const maxItems = (getters.isFiltered || locations[0]?.distance) ? state.maxShownLocations : -1;
            const result = new Set<LocationInterface>();
            if (maxItems < 0) return state.filteredLocations;
            const activeFilters = Array.from(state.activeFilters);

            locations = locations.sort((locationA, locationB) => {
                if (locationA.distance) return 0;
                const positionA = sortByMarket.indexOf(locationA.keywords?.locationType || '');
                const positionB = sortByMarket.indexOf(locationB.keywords?.locationType || '');
                return positionA > positionB ? 1 : -1;
            })

            for (const location of locations) {
                if (activeFilters && activeFilters.some(el => sortByMarket.includes(el.type)) && !sortByMarket.includes(location.keywords?.locationType || '')) {
                   continue
                }

                result.add(location);
                if (result.size >= maxItems) {
                    return result;
                }
            }

            return result;
        },
        filteredLocationsCount(state: StoreInterface): number {
            return state.filteredLocations.size;
        },
        locations(state: StoreInterface): Set<LocationInterface> {
            return state.locations;
        },
        mapsAPIKey(state: StoreInterface): string {
            return state.mapsAPIKey;
        },
        favorite(state: StoreInterface): string | null {
            return state.favorite;
        },
        isFiltered(state: StoreInterface): boolean {
            return state.searchTerm.length > 0
                || state.activeFilters.size > 0
                || state.activeFeatures.size > 0
        },
        maxShownLocations(state: StoreInterface): number {
            return state.maxShownLocations;
        },
        config(state: StoreInterface): ConfigParameter {
            return state.config;
        },
        map(state: StoreInterface): google.maps.Map | null {
            return state.map;
        }
    }
})
